import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  TextField,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Business,
  Comment,
  AccessTime,
  Search,
  Refresh
} from '@mui/icons-material';
import { fetchDiscussionPosts, transformDiscussionData } from '../../Utils/leetCodeApi';
import SEOHead from '../SEO/SEOHead';

// Local storage keys
const STORAGE_KEYS = {
  DISCUSSIONS: 'leetcode_discussions',
  LAST_SEARCH: 'leetcode_last_search',
  LAST_SEARCH_TIME: 'leetcode_last_search_time',
  SEARCH_HISTORY: 'leetcode_search_history'
};

// Cache expiration time (24 hours in milliseconds)
const CACHE_EXPIRY = 24 * 60 * 60 * 1000;

const DiscussionList = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [discussions, setDiscussions] = useState([]);
  const [filteredDiscussions, setFilteredDiscussions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSearchedCompany, setLastSearchedCompany] = useState('');
  const [searchHistory, setSearchHistory] = useState([]);

  // Load cached data on component mount
  useEffect(() => {
    loadCachedData();
  }, []);

  // Load cached discussions and search history
  const loadCachedData = () => {
    try {
      // Load cached discussions
      const cachedDiscussions = localStorage.getItem(STORAGE_KEYS.DISCUSSIONS);
      if (cachedDiscussions) {
        const parsed = JSON.parse(cachedDiscussions);
        // Check if cache is still valid
        if (parsed.timestamp && (Date.now() - parsed.timestamp) < CACHE_EXPIRY) {
          setDiscussions(parsed.data || []);
        } else {
          // Cache expired, clear it
          localStorage.removeItem(STORAGE_KEYS.DISCUSSIONS);
        }
      }

      // Load last search
      const lastSearch = localStorage.getItem(STORAGE_KEYS.LAST_SEARCH);
      if (lastSearch) {
        setLastSearchedCompany(lastSearch);
        setSearchQuery(lastSearch);
      }

      // Load search history
      const history = localStorage.getItem(STORAGE_KEYS.SEARCH_HISTORY);
      if (history) {
        setSearchHistory(JSON.parse(history));
      }
    } catch (error) {
      console.error('Error loading cached data:', error);
    }
  };

  // Save discussions to cache
  const saveToCache = (data, companyName) => {
    try {
      const cacheData = {
        data,
        company: companyName,
        timestamp: Date.now()
      };
      localStorage.setItem(STORAGE_KEYS.DISCUSSIONS, JSON.stringify(cacheData));
      
      // Save last search
      localStorage.setItem(STORAGE_KEYS.LAST_SEARCH, companyName);
      
      // Update search history
      const newHistory = [companyName, ...searchHistory.filter(item => item !== companyName)].slice(0, 10);
      setSearchHistory(newHistory);
      localStorage.setItem(STORAGE_KEYS.SEARCH_HISTORY, JSON.stringify(newHistory));
      
    } catch (error) {
      console.error('Error saving to cache:', error);
    }
  };

  // Filter discussions based on search query
  useEffect(() => {
    let filtered = discussions;
    
    // Apply search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(discussion => 
        discussion.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        discussion.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        discussion.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    setFilteredDiscussions(filtered);
  }, [discussions, searchQuery]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a company name to search');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const leetCodeData = await fetchDiscussionPosts(searchQuery.trim());
      const transformedDiscussions = leetCodeData
        .map(edge => transformDiscussionData(edge.node))
        .filter(discussion => discussion !== null);
      
      setDiscussions(transformedDiscussions);
      setLastSearchedCompany(searchQuery.trim());
      
      // Save to cache
      saveToCache(transformedDiscussions, searchQuery.trim());
      
    } catch (error) {
      console.error('Error searching discussions:', error);
      setError('Failed to fetch discussions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (lastSearchedCompany) {
      setLoading(true);
      setError(null);
      
      try {
        const leetCodeData = await fetchDiscussionPosts(lastSearchedCompany);
        const transformedDiscussions = leetCodeData
          .map(edge => transformDiscussionData(edge.node))
          .filter(discussion => discussion !== null);
        
        setDiscussions(transformedDiscussions);
        
        // Update cache with fresh data
        saveToCache(transformedDiscussions, lastSearchedCompany);
        
      } catch (error) {
        console.error('Error refreshing discussions:', error);
        setError('Failed to refresh discussions. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleHistoryClick = (companyName) => {
    setSearchQuery(companyName);
    handleSearch();
  };

  const clearCache = () => {
    try {
      localStorage.removeItem(STORAGE_KEYS.DISCUSSIONS);
      localStorage.removeItem(STORAGE_KEYS.LAST_SEARCH);
      localStorage.removeItem(STORAGE_KEYS.SEARCH_HISTORY);
      setDiscussions([]);
      setLastSearchedCompany('');
      setSearchHistory([]);
      setSearchQuery('');
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  const handleDiscussionClick = (discussionId) => {
    navigate(`/discussions/${discussionId}`);
  };

  return (
    <Container maxWidth="lg" sx={{ marginTop: '20px' }}>
      <SEOHead 
        title="Tech Interview Discussions & Q&A Community | SlazySloth"
        description="Join the tech interview community. Ask questions, share experiences, and get help with DSA, system design, and coding interview preparation. Connect with fellow developers and engineers."
        keywords="tech interview discussions, coding interview community, programming Q&A, developer community, interview experiences, coding help, system design discussions, DSA questions, interview preparation community"
        url="https://slazysloth.com/discussions"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "DiscussionForumPosting",
          "name": "Tech Interview Discussions",
          "description": "Community forum for tech interview preparation and coding discussions",
          "provider": {
            "@type": "Organization",
            "name": "SlazySloth"
          },
          "about": ["Tech Interviews", "Coding", "System Design", "Career Advice"]
        }}
      />
      
      {/* Header Section */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography component="h1" variant="h3" sx={{ fontWeight: 'bold', mb: 2, color: '#333' }}>
          Tech Interview Discussions
        </Typography>
        <Typography variant="h6" sx={{ color: '#666', maxWidth: 800, mx: 'auto', mb: 3 }}>
          Connect with the community, ask questions, and share your interview experiences. 
          Get help with coding problems, system design, and career advice.
        </Typography>
      </Box>

      {/* Search Section */}
      <Box sx={{ mb: 4, p: 3, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
        <Typography variant="h5" sx={{ mb: 3, color: '#008080', fontWeight: 'bold' }}>
          Search LeetCode Discussions
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Enter Company Name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="e.g., project44, Google, Amazon"
            sx={{ minWidth: 300 }}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={handleSearch}
            disabled={loading || !searchQuery.trim()}
            sx={{ 
              backgroundColor: '#008080',
              '&:hover': { backgroundColor: '#006666' }
            }}
          >
            Search
          </Button>
          {lastSearchedCompany && (
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={loading}
              sx={{ borderColor: '#008080', color: '#008080' }}
            >
              Refresh
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={clearCache}
            sx={{ borderColor: '#dc3545', color: '#dc3545' }}
          >
            Clear Cache
          </Button>
        </Box>
        
        {lastSearchedCompany && (
          <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
            Showing results for: <strong>{lastSearchedCompany}</strong>
          </Typography>
        )}

        {/* Search History */}
        {searchHistory.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
              Recent searches:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {searchHistory.map((company, index) => (
                <Chip
                  key={index}
                  label={company}
                  size="small"
                  onClick={() => handleHistoryClick(company)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    '&:hover': { backgroundColor: '#bbdefb' }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#008080' }} />
        </Box>
      )}

      {/* Discussions List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!loading && filteredDiscussions.length === 0 && discussions.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              Search for a company to find LeetCode discussions
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Try searching for companies like "project44", "Google", "Amazon", etc.
            </Typography>
          </Box>
        )}

        {!loading && filteredDiscussions.length === 0 && discussions.length > 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No discussions found matching your criteria
            </Typography>
          </Box>
        )}

        {filteredDiscussions.map((discussion) => (
          <Card 
            key={discussion.id}
            sx={{ 
              cursor: 'pointer',
              borderRadius: '8px',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4
              }
            }}
            onClick={() => handleDiscussionClick(discussion.id)}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 'bold', 
                    color: '#008080',
                    mb: 1
                  }}>
                    {discussion.title}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ 
                    color: '#333', 
                    mb: 2,
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {discussion.description}
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {discussion.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        sx={{
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          fontSize: '0.75rem',
                          height: '24px'
                        }}
                      />
                    ))}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                        {discussion.authorAvatar}
                      </Avatar>
                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold' }}>
                        {discussion.author}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Comment sx={{ fontSize: 16, color: '#999' }} />
                      <Typography variant="body2" sx={{ color: '#999', fontSize: '0.75rem' }}>
                        {discussion.comments}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" sx={{ color: '#999', fontSize: '0.75rem' }}>
                      {formatDate(discussion.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Container>
  );
};

export default DiscussionList;
